import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import React from "react";
import Timeago from "react-timeago";
import { HEARTBEAT_WARNING_LIMIT } from "src/config";
import IQVeraZone from "src/ServerEntities/IQVeraZone";
import styled from "styled-components";

const TableContainer = styled.div`
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    border-radius: 16px;
    border: 4px solid #1577ae;
    box-sizing: border-box;
    padding: 0 0 12px;
    margin: 12px;
    justify-content: start;
    & h3 {
        margin: 0 0 10px;
        font-size: 1.1rem;
        text-align: center;
        font-weight: 600;
    }
`;

const Header = styled.header`
    margin: 0 0 10px;
    text-align: center;
    padding: 6px;
    border-radius: 10px 10px 0 0;
    background-color: #1577ae;
    color: #ffffff;
    display: flex;
    flex-direction: row;
    align-items: space-between;
    cursor: pointer;
    & h2 {
        margin: 0;
        font-size: 1.2rem;
        width: 100%;
        text-align: center;
    }
`;

const HearbeatBar = styled.div`
    width: 150px;
    min-height: 30px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 4px 12px;
    margin: 4px 12px;
    text-align: center;
    & h4 {
        width: 100%;
        margin: 0;
        font-size: 1rem;
        f0ont-weight: 600;
    }
`;

const ContentRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 12px;
    justify-content: space-around;
`;

const QueueDetails = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    & h4 {
        background-color: #00b0f0;
        margin: 0 6px 4px;
        font-size: 1rem;
        font-weight: normal;
        padding: 6px 12px;
        border-radius: 10px;
    }
    & p {
        margin: 0;
    }
`;

interface IProps {
    zone: IQVeraZone
};

const Zone = (props: IProps) => {
    const [open, setOpen] = React.useState(false);
    const z = props.zone;

    const changeOpen = () => {
        setOpen(!open);
    };

    const isValidHeartbeat = z.lastHeartbeat && !isNaN(z.lastHeartbeat) && z.lastHeartbeat > 0;
    const isValidLastMessage = z.lastMessageReceived && !isNaN(z.lastMessageReceived) && z.lastMessageReceived > 0;
    const isValidLastError = z.lastMessageError && !isNaN(z.lastMessageError) && z.lastMessageError > 0;
    const heartbeatHealthy = isValidHeartbeat && z.lastHeartbeat > (DateTime.now().toMillis() - HEARTBEAT_WARNING_LIMIT);
    return <TableContainer>
        <Header onClick={changeOpen}>
            <h2>{z.zoneName}</h2>
            <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} size='2x' />
        </Header>
        <h3>{z.zoneDescription}</h3>
        <ContentRow>
            <HearbeatBar style={{ backgroundColor: heartbeatHealthy ? '#5CEA5A' : '#FF0000' }}>
                <h4>Last heartbeat</h4>
                {isValidHeartbeat ? <Timeago date={z.lastHeartbeat} /> : <span>No heartbeat data</span>}
            </HearbeatBar>
            <HearbeatBar style={{ backgroundColor: '#cccccc' }}>
                <h4>Last message</h4>
                {isValidLastMessage ? <Timeago date={z.lastMessageReceived} /> : <span>None received</span>}
            </HearbeatBar>
            <HearbeatBar style={{ backgroundColor: '#cccccc' }}>
                <h4>Last error</h4>
                {isValidLastError ? <Timeago date={z.lastMessageError} /> : <span>None received</span>}
            </HearbeatBar>
        </ContentRow>
        {open &&
            <>
                <ContentRow>
                    <HearbeatBar style={{ backgroundColor: '#5CEA5A' }}>
                        <h4>Total channels</h4>
                        {z.totalChannels}
                    </HearbeatBar>
                    <HearbeatBar style={{ backgroundColor: '#cccccc' }}>
                        <h4>Running</h4>
                        {z.totalChannelsRunning}
                    </HearbeatBar>
                    <HearbeatBar style={{ backgroundColor: '#cccccc' }}>
                        <h4>Stopped</h4>
                        {z.totalChannelsStopped}
                    </HearbeatBar>
                </ContentRow>
                <ContentRow>
                    <QueueDetails>
                        <h4>Received</h4>
                        <p>{z.totalMessagesReceived}</p>
                    </QueueDetails>
                    <QueueDetails>
                        <h4>Inbound</h4>
                        <p>{z.totalMessagesInboundQueue}</p>
                    </QueueDetails>
                    <QueueDetails>
                        <h4>Processing</h4>
                        <p>{z.totalMessagesProcessingQueue}</p>
                    </QueueDetails>
                    <QueueDetails>
                        <h4>Outbound</h4>
                        <p>{z.totalMessagesOutboundQueue}</p>
                    </QueueDetails>
                    <QueueDetails>
                        <h4>Errored</h4>
                        <p>{z.totalMessagesErrorQueue}</p>
                    </QueueDetails>
                </ContentRow>
            </>
        }
    </TableContainer>;
}

export default Zone;